
import { useSyncExternalStore } from "react";
import { WifiOff } from "lucide-react";
import { cn } from "@/lib/cn";

const subscribe = (cb: () => void) => {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
};

/** True while the browser reports a network connection (always true on the server). */
export function useOnline() {
  return useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  );
}

/**
 * Strip at the top of the page content when the network drops (« Hors ligne »): reading
 * stays possible, actions wait for the network. Two looks, as the boards draw them:
 *  - `amber` (default): yellow strip, « Connexion perdue : vous pouvez lire cette page… »;
 *  - `dark`: #1B1C31 strip, 36 px, lime icon (FA-Clients « Vous êtes hors ligne. La liste
 *    affichée est celle du dernier chargement. », the « Vous êtes hors ligne » proposals).
 */
export function OfflineStrip({ className, variant = "amber", message = "Connexion perdue : vous pouvez lire cette page, mais les actions attendront le retour du réseau." }: { className?: string; variant?: "amber" | "dark"; message?: string }) {
  const online = useOnline();
  if (online) return null;
  return (
    <div
      role="status"
      className={cn(
        "mb-4 flex items-center gap-2 rounded-xl px-3.5 text-[12.5px] font-semibold",
        variant === "dark" ? "min-h-9 bg-ink py-2 text-white" : "bg-[#FEF3C7] py-2.5 text-warning",
        className,
      )}
    >
      <WifiOff className={cn("size-3.5 shrink-0", variant === "dark" && "text-lime")} strokeWidth={2.2} />
      <span className="min-w-0 break-words">{message}</span>
    </div>
  );
}
